import React from "react";
import IntegrationConversionSettings from "./IntegrationConversionSettings";
import {Box, Button, DialogActions, DialogContent, List, StepConnector, TextField, useMediaQuery, useTheme, Step, StepLabel, Stepper, Link} from "@mui/material";
import {IntegrationDialog as Dialog, IntegrationHeader, IntegrationBackButton} from "./IntegrationDesign";
import {integrationActions} from "../../../store/actions/integrationAction";
import {connect} from "react-redux";
import {useCallback, useEffect, useState} from "react";
import Spinner from "../../spinner/Spinner";
import {Check} from "@mui/icons-material";
import {useFieldArray, useForm} from "react-hook-form";
import {alertActions} from "../../../store/actions/alertActions";
import {CustomListItem, CustomListItemIcon, CustomListItemText} from "./RevContentIntegration";
import ReactHookFormSelect from "../../input/ReactHookFormSelect";

function PopinIntegration(props) {
    const theme = useTheme();
    const {integration, resetIntegration, create, update, find, alert, removeIntegrationEvent} = props;

    const steps = ["Integrate Ad Account", "Conversion API"];

    const integrationId = parseInt(props?.match?.params?.id);

    const [open, setOpen] = useState(true);
    const [activeStep, setActiveStep] = useState(1);
    const [removeEventIndex, setRemoveEventIndex] = useState(null);
    const [step1Data, setStep1Data] = useState(null);

    const {register, handleSubmit, control, setValue, watch, getValues, formState: {errors}} = useForm();
    const {fields, remove, append} = useFieldArray({
        control,
        name: "events"
    });

    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

    const handleClose = useCallback((event, reason) => {
        if (reason === 'backdropClick') {
            return false;
        }

        resetIntegration();
        setOpen(false);
        props.history.push({pathname: "/settings/integrations", state: {from: props.location}});
    }, [resetIntegration, props.history, props.location]);

    const handleOnSubmitStep1 = (data) => {
        setStep1Data(data);
        setActiveStep(2);
    };

    const handleOnSubmit = (data) => {
        if (integrationId > 0) {
            update({
                integrationId: integrationId,
                name: step1Data?.name,
                apiKey: step1Data?.apiKey,
                adAccountId: step1Data?.adAccountId,
                currency: step1Data?.currency,
                enableConversionAPI: data?.enableConversionAPI,
                events: data?.events,
            });
        } else {
            create({
                name: step1Data?.name,
                apiKey: step1Data?.apiKey,
                adAccountId: step1Data?.adAccountId,
                currency: step1Data?.currency,
                enableConversionAPI: data?.enableConversionAPI,
                events: data?.events,
            });
        }
    };

    const appendNewEvent = () => {
        append({
            eventId: 0,
            name: "",
            includePayout: false
        });
    };

    const handleDeleteIntegrationEvent = useCallback(({eventId, index}) => {
        if (eventId === 0) {
            remove(index);
            return false;
        }
        setRemoveEventIndex({eventId, index});
    }, [remove, setRemoveEventIndex]);

    useEffect(() => {
        if (removeEventIndex !== null) {
            remove(removeEventIndex?.index);
            removeIntegrationEvent({integrationId, eventId: removeEventIndex?.eventId});
        }
    }, [integrationId, remove, removeEventIndex, removeIntegrationEvent]);

    useEffect(() => {
        if (integrationId > 0) {
            find(integrationId);
        }
    }, [find, integrationId]);

    useEffect(() => {
        if (activeStep === 1 && integrationId > 0 && integration?.currentIntegration !== null) {
            if (integration?.currentIntegration?.id === integrationId) {
                setValue("name", integration?.currentIntegration?.networkName);
                for (const cred of integration?.currentIntegration?.integrationCredentials) {
                    if (cred?.keyName === "popin_api_key") {
                        setValue("apiKey", cred?.keyValue);
                    }
                    if (cred?.keyName === "popin_ad_account_currency") {
                        setValue("currency", cred?.keyValue);
                    }
                    if (cred?.keyName === "popin_ad_account_id") {
                        setValue("adAccountId", cred?.keyValue);
                    }
                }
            }
        }
    }, [activeStep, setValue, integration?.currentIntegration, integrationId]);

    // Set step 1 form data.
    useEffect(() => {
        if (activeStep === 1 && step1Data !== null) {
            setValue("name", step1Data?.name);
            setValue("apiKey", step1Data?.apiKey);
            setValue("adAccountId", step1Data?.adAccountId);
            setValue("currency", step1Data?.currency);
        }
    }, [activeStep, setValue, step1Data]);

    useEffect(() => {
        if (activeStep === 2 && integration?.currentIntegration !== null && getValues("events")?.length === 0) {
            for (const cred of integration?.currentIntegration?.integrationCredentials) {
                if (cred?.keyName === "popin_enable_conversion_api") {
                    if (cred?.keyValue === "true") {
                        setValue("enableConversionAPI", true);
                    } else {
                        setValue("enableConversionAPI", false);
                    }
                }
            }
            let events = [];
            integration?.currentIntegration?.integrationEvents?.forEach((event) => {
                events.push({
                    eventId: event?.id,
                    name: event?.name,
                    includePayout: event?.includePayout
                });
            });
            setValue("events", events);
        }
    }, [activeStep, setValue, integration?.currentIntegration, getValues]);

    useEffect(() => {
        if (alert?.successMsg !== "") {
            handleClose();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [alert?.successMsg, handleClose]);

    return (
        <Dialog open={open}
                onClose={handleClose}
                fullWidth={true}
                fullScreen={fullScreen}
                maxWidth={"sm"}
                aria-labelledby="popin-ad-account-integration"
        >
            <IntegrationHeader name="Popin" logo="/assets/images/traffic-sources/popin-white.svg" id="popin-ad-account-integration"/>
            <DialogContent>
                <Stepper
                    alternativeLabel={fullScreen}
                    activeStep={activeStep - 1}
                    connector={
                        <StepConnector
                            sx={{"& .MuiStepConnector-line ": {borderColor: theme.palette.background.tableBorder}}}
                        />}
                    sx={{
                        margin: "0 auto",
                        width: "100%",
                        maxWidth: 800,
                        padding: "0px 24px 24px 24px"
                    }}
                >
                    {steps.map((label) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

                {integration?.loading?.integration && <Spinner overComponentBox={true}/>}

                {activeStep === 1 &&
                    <>
                        <p>
                            Please check our integration
                            guide <Link target="_blank" underline="hover" rel="noreferrer"
                                        href="https://skro.eu/docs/integrations/popin-and-skro">here</Link>.
                        </p>

                        <form onSubmit={handleSubmit(handleOnSubmitStep1)} autoComplete={"off"}>
                            <Box display="flex" flexWrap={"wrap"}>
                                <Box py={0.75} width="100%">
                                    <TextField
                                        error={!!errors.name}
                                        variant="outlined"
                                        margin="dense"
                                        id="name"
                                        label="Integration Name*"
                                        {...register("name", {required: true, maxLength: 255, minLength: 2})}
                                        name="name"
                                        size="small"
                                        type="text"
                                        placeholder="Enter your integration name"
                                        fullWidth={true}
                                        defaultValue="Popin"
                                        slotProps={{
                                            inputLabel: {
                                                shrink: true
                                            }
                                        }}
                                    />
                                </Box>
                                <Box py={0.75} width="100%">
                                    <TextField
                                        error={!!errors.apiKey}
                                        variant="outlined"
                                        margin="dense"
                                        id="apiKey"
                                        label="API Key*"
                                        {...register("apiKey", {required: true, maxLength: 1024, minLength: 2})}
                                        name="apiKey"
                                        size="small"
                                        type="text"
                                        placeholder="Enter your API Key"
                                        fullWidth={true}
                                        slotProps={{
                                            inputLabel: {
                                                shrink: true
                                            }
                                        }}
                                    />
                                </Box>
                                <Box py={0.75} width="100%">
                                    <TextField
                                        error={!!errors.adAccountId}
                                        variant="outlined"
                                        margin="dense"
                                        id="adAccountId"
                                        label="Ad Account ID*"
                                        {...register("adAccountId", {required: true, maxLength: 1024, minLength: 2})}
                                        name="adAccountId"
                                        size="small"
                                        type="text"
                                        placeholder="Enter your Ad Account ID"
                                        fullWidth={true}
                                        slotProps={{
                                            inputLabel: {
                                                shrink: true
                                            }
                                        }}
                                    />
                                </Box>
                                <Box py={0.75} width="100%">
                                    <ReactHookFormSelect
                                        rules={{required: true}}
                                        name="currency"
                                        control={control}
                                        setValue={setValue}
                                        label="Ad Account Currency*"
                                        placeholder="Your Ad Account account currency"
                                        defaultValue="USD"
                                        options={[
                                            {"label": "USD", "value": "USD"},
                                        ]}
                                    />
                                </Box>
                            </Box>
                        </form>

                        <List>
                            <CustomListItem>
                                <CustomListItemIcon>
                                    <Check color={"primary"}/>
                                </CustomListItemIcon>
                                <CustomListItemText>
                                    Check the status of your campaigns and sites.
                                </CustomListItemText>
                            </CustomListItem>
                            <CustomListItem>
                                <CustomListItemIcon>
                                    <Check color={"primary"}/>
                                </CustomListItemIcon>
                                <CustomListItemText>
                                    Change daily budget and bidding.
                                </CustomListItemText>
                            </CustomListItem>
                            <CustomListItem>
                                <CustomListItemIcon>
                                    <Check color={"primary"}/>
                                </CustomListItemIcon>
                                <CustomListItemText>
                                    Pause or resume campaigns and sites.
                                </CustomListItemText>
                            </CustomListItem>
                            <CustomListItem>
                                <CustomListItemIcon>
                                    <Check color={"primary"}/>
                                </CustomListItemIcon>
                                <CustomListItemText>
                                    Check delivery status of campaigns and sites.
                                </CustomListItemText>
                            </CustomListItem>
                            <CustomListItem>
                                <CustomListItemIcon>
                                    <Check color={"primary"}/>
                                </CustomListItemIcon>
                                <CustomListItemText>
                                    Sync cost on site level every hour.
                                </CustomListItemText>
                            </CustomListItem>
                        </List>
                    </>
                }

                {activeStep === 2 &&
                    <Box sx={{position: "relative"}}>
                        {integration?.loading?.conversionApi && <Spinner overComponentBox={true}/>}
                        <IntegrationConversionSettings provider="Popin" control={control} register={register} errors={errors}
                            enabled={watch("enableConversionAPI")} eventFields={fields}
                            onAddEvent={appendNewEvent} onDeleteEvent={handleDeleteIntegrationEvent}
                            eventLabel="Conversion Type Value" eventPlaceholder="Enter Popin conversion type value"/>
                    </Box>
                }
            </DialogContent>
            <DialogActions>
                <Box display="flex" justifyContent="flex-end">
                    <Box mr={3}>
                        <Button onClick={handleClose} color="inherit" variant="text">
                            Close
                        </Button>
                    </Box>
                    {activeStep === 1 &&
                        <Box>
                            <Button onClick={handleSubmit(handleOnSubmitStep1)} color="primary" variant="contained">
                                Next
                            </Button>
                        </Box>
                    }
                    {activeStep === 2 &&
                        <>
                            <Box mr={1}>
                                <IntegrationBackButton onClick={() => setActiveStep(1)}/>
                            </Box>
                            <Box>
                                <Button onClick={handleSubmit(handleOnSubmit)} color="primary" variant="contained">
                                    Save & Close
                                </Button>
                            </Box>
                        </>
                    }
                </Box>
            </DialogActions>
        </Dialog>
    );
}

const mapStateToProps = (state) => {
    return {
        integration: state.integration,
        alert: state.alert
    };
};

const mapDispatchToProps = {
    resetIntegration: integrationActions.reset,
    find: integrationActions.findIntegration,
    create: integrationActions.createPopinIntegration,
    update: integrationActions.updatePopinIntegration,
    error: alertActions.error,
    removeIntegrationEvent: integrationActions.removeIntegrationEventFromCurrentIntegration
};

export default connect(mapStateToProps, mapDispatchToProps)(PopinIntegration);
